import type {ReactElement} from 'react';
import React from 'react';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {RGBA} from '@Utils/color.util';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromEffectsActiveItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveItem';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromEffectsOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveSlideItem';

interface TintColorPanelProps {
  panelId: string;
  onBack(): void;
  onClose(): void;
}

export function TintColorPanel({...props}: TintColorPanelProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemTintColor = useGetKeyFromEffectsActiveItem('tintColor');

  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const slideItemTintColor = useGetKeyFromEffectsOfActiveSlideItem('tintColor');

  if (!activeItem) {
    return null;
  }

  if (activeItem.isSlideshow() && !activeSlide) {
    return null;
  }

  const onChangeSlideItemTintColor = (fillColor: RGBA[], undoable = true): void => {
    if (!activeItem.isSlideshow() || !activeSlide?.isMediaSlide()) {
      return;
    }
    void activeItem.updateSlideFromObject(
      activeSlide.uid,
      {
        effects: {
          tintColor: fillColor[0],
        },
      },
      {
        undoable,
      }
    );
  };

  const onChangeTintColor = (fillColor: RGBA[], undoable = true): void => {
    if (!activeItem || !('effects' in activeItem)) {
      return;
    }
    void activeItem.updateFromObject(
      {
        effects: {
          tintColor: fillColor[0],
        },
      },
      {
        undoable,
      }
    );
  };

  const getColorPanel = (): ReactElement | null => {
    if (activeItem.isSlideshow() && activeSlide?.isMediaSlide() && slideItemTintColor) {
      return (
        <PosterEditorColorPanel
          headerText={window.i18next.t('pmwjs_tint')}
          onBackButton={(): void => {
            props.onBack();
          }}
          panelId={props.panelId}
          fillObject={{
            fillType: FillTypes.SOLID,
            fillColor: [slideItemTintColor],
          }}
          onChangeColor={onChangeSlideItemTintColor}
          onClose={(): void => {
            props.onClose();
          }}
        />
      );
    }

    if (activeItem && 'effects' in activeItem && activeItemTintColor) {
      return (
        <PosterEditorColorPanel
          headerText={window.i18next.t('pmwjs_tint')}
          onBackButton={(): void => {
            props.onBack();
          }}
          panelId={props.panelId}
          fillObject={{
            fillType: FillTypes.SOLID,
            fillColor: [activeItemTintColor],
          }}
          onChangeColor={onChangeTintColor}
          onClose={(): void => {
            props.onClose();
          }}
        />
      );
    }

    return null;
  };

  return getColorPanel();
}
